import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Box, Checkbox, IconButton, InputAdornment, List, ListItemButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { GridColumnVisibilityModel, GridValidRowModel } from "@mui/x-data-grid";
import { type SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { INK, NEUTRAL } from "../../../../theme/tokens";
import { ToolbarTriggerButton } from "../../Clickable/ToolbarTriggerButton";
import { FILTER_SEARCH_CANCEL_BUTTON_SX, filterSearchHtmlInputProps } from "../../Inputs/filterSearchFieldProps";
import { SystemValidationTextField } from "../../Inputs/SystemValidationTextField";
import { SystemReactTinyPopover } from "../../Popups/SystemReactTinyPopover";
import SystemButton from "../../SystemButton/SystemButton";
import type { SyntheticPickerColumn, SystemGridColDef } from "./types";

/**
 * Normalized picker row — a real column and a synthetic (multi-field) entry both render the same way.
 * `fields` are the visibility-model keys the checkbox toggles together (real columns have exactly one).
 */
interface PickerEntry {
	/** React key: the field for a real column, `synthetic:${id}` for a synthetic entry. */
	key: string;
	label: string;
	/** Model keys toggled together by this row (real: `[field]`; synthetic: all controlled fields). */
	fields: string[];
	group: string | undefined;
	/** Trend companion field (real columns only). */
	trendField: string | undefined;
	/** Identifier used to build the row's testid (real: field; synthetic: id). */
	testId: string;
}

export interface ColumnPickerPopoverProps<T extends GridValidRowModel> {
	/** The full column set; the picker lists all columns except those flagged `excludeFromColumnPicker`. */
	columns: SystemGridColDef<T>[];
	/** Synthetic rows appended to the list — each toggles a set of real column fields in lockstep. */
	syntheticColumns?: SyntheticPickerColumn[];
	visibilityModel: GridColumnVisibilityModel;
	/** Apply a new model (single source of truth lives in the grid's `useColumnVisibility`). */
	onChange: (model: GridColumnVisibilityModel) => void;
	/** Restore the curated default (or show-all). Owned by the grid so persistence stays in one place. */
	onReset: () => void;
	/** Pre-translated `groupKey → label`; presence groups the list (key order = section order). */
	groupLabels?: Record<string, string>;
	/**
	 * Grouped mode only: a tri-state "Select all" checkbox on each section header toggling every entry
	 * currently listed in that section (search-filtered rows only). See `ColumnPickerOptions.groupSelectAll`.
	 */
	groupSelectAll?: boolean;
	/** Metric `field` → companion trend `field`; renders the inline trend checkbox when set. */
	trendColumnMatcher?: (field: string) => string | undefined;
	/** Disables the trigger (and keeps the popover closed) — e.g. while the grid is loading. */
	disabled?: boolean;
	/**
	 * z-index for the popover container (react-tiny-popover portals to `<body>` with no z-index of its own).
	 * Set it above MUI's modal layer (1300) so the picker isn't occluded when the grid renders inside a Dialog.
	 */
	zIndex?: number | string;
	/** Testid prefix, e.g. `${dataTestId}-column-picker`. */
	testIdBase?: string;
	onlyShowIcon?: boolean;
}

/**
 * Generic, toolbar-mounted column picker for `SystemDataGrid`: a "Columns" button opens a searchable
 * popover of per-column checkboxes with a visible-count badge and a reset-to-default footer. Flat by
 * default; grouped into labeled sections when `groupLabels` is supplied. An optional `trendColumnMatcher`
 * pairs each metric with a companion "trend" column via an inline secondary checkbox. Colors track the
 * app theme (`primary.main`) — the checkbox default from the theme is intentionally left untouched.
 */
export function ColumnPickerPopover<T extends GridValidRowModel>({
	columns,
	syntheticColumns,
	visibilityModel,
	onChange,
	onReset,
	groupLabels,
	groupSelectAll,
	trendColumnMatcher,
	zIndex,
	disabled,
	testIdBase,
	onlyShowIcon = false,
}: ColumnPickerPopoverProps<T>) {
	const { t } = useTranslation();
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [search, setSearch] = useState("");
	const open = !!anchorEl;

	// Real columns that opt into the picker (identity/structural/trend columns set `excludeFromColumnPicker`),
	// plus any synthetic multi-field rows — normalized to one shape so both render/toggle identically.
	const entries = useMemo<PickerEntry[]>(() => {
		const real: PickerEntry[] = columns
			.filter((c) => !c.excludeFromColumnPicker)
			.map((c) => {
				const field = String(c.field);
				return {
					key: field,
					label: String(c.headerName ?? field),
					fields: [field],
					group: c.columnPickerGroup,
					trendField: trendColumnMatcher?.(field),
					testId: field,
				};
			});
		const synthetic: PickerEntry[] = (syntheticColumns ?? []).map((s) => ({
			key: `synthetic:${s.id}`,
			label: s.label,
			fields: s.controlledFields,
			group: s.columnPickerGroup,
			trendField: undefined,
			testId: s.id,
		}));
		return [...real, ...synthetic];
	}, [columns, syntheticColumns, trendColumnMatcher]);

	// An entry is visible only when every field it controls is visible (a synthetic row is "on" only
	// while its whole block shows). Only `false` hides: an absent key and an explicit `true` both read
	// as visible (see `toggleEntry` for why a shown column is written as an explicit `true`).
	const isEntryVisible = useCallback((entry: PickerEntry) => entry.fields.every((f) => visibilityModel[f] !== false), [visibilityModel]);

	// Normalize flat and grouped modes to the same shape: a list of sections. Flat mode is a single
	// header-less section; grouped mode follows `groupLabels` insertion order and drops empty groups.
	const groupedEntries = useMemo(() => {
		if (!groupLabels) return [{ key: "__all", label: undefined as string | undefined, rows: entries }];
		const byGroup = new Map<string, PickerEntry[]>();
		for (const entry of entries) {
			const group = entry.group;
			if (!group || !(group in groupLabels)) continue;
			if (!byGroup.has(group)) byGroup.set(group, []);
			byGroup.get(group)?.push(entry);
		}
		return Object.keys(groupLabels)
			.map((key) => ({ key, label: groupLabels[key], rows: byGroup.get(key) ?? [] }))
			.filter((g) => g.rows.length > 0);
	}, [groupLabels, entries]);

	const filteredGroups = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return groupedEntries;
		return groupedEntries.map((g) => ({ ...g, rows: g.rows.filter((r) => r.label.toLowerCase().includes(q)) })).filter((g) => g.rows.length > 0);
	}, [groupedEntries, search]);

	const visibleCount = useMemo(() => entries.filter(isEntryVisible).length, [entries, isEntryVisible]);

	const toggleEntries = useCallback(
		(targets: PickerEntry[], visible: boolean) => {
			const next = { ...visibilityModel };
			const apply = (f: string) => {
				next[f] = visible;
			};
			for (const entry of targets) {
				for (const field of entry.fields) apply(field);
				// Keep a metric and its trend companion in lockstep — a lone trend pill with no metric is noise.
				if (entry.trendField) apply(entry.trendField);
			}
			onChange(next);
		},
		[visibilityModel, onChange],
	);

	const toggleEntry = useCallback((entry: PickerEntry, visible: boolean) => toggleEntries([entry], visible), [toggleEntries]);

	const showGroupSelectAll = !!groupSelectAll && !!groupLabels;

	// Toggles only the trend companion, leaving the parent metric visible.
	const toggleTrend = useCallback(
		(trendField: string, visible: boolean) => {
			const next = { ...visibilityModel, [trendField]: visible };
			onChange(next);
		},
		[visibilityModel, onChange],
	);

	const closePopover = useCallback((e?: SyntheticEvent | MouseEvent) => {
		e?.stopPropagation();
		setAnchorEl(null);
		// Reset the query too: a stale one survives reopening and reads as "no columns".
		setSearch("");
	}, []);

	return (
		<SystemReactTinyPopover
			align={"center"}
			isOpen={open && !disabled}
			onClickOutside={closePopover}
			positions={["right", "bottom", "left", "top"]}
			// react-tiny-popover portals to <body> with no z-index; lift the container above MUI's modal layer
			// so the picker isn't hidden behind a Dialog (mirrors the sort popup).
			containerStyle={zIndex != null ? { zIndex: String(zIndex) } : undefined}
			content={
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					<Box sx={{ borderBottom: `1px solid ${NEUTRAL[100]}` }}>
						<SystemValidationTextField
							autoFocus
							fullWidth
							placeholder={t("system.dataGrid.searchColumns")}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							dataTestId={testIdBase ? `${testIdBase}-search` : undefined}
							inputType="search"
							injectedProps={{
								slotProps: {
									htmlInput: filterSearchHtmlInputProps("column-picker-search"),
									input: {
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon sx={{ fontSize: 18, color: NEUTRAL[400] }} />
											</InputAdornment>
										),
										endAdornment: search ? (
											<InputAdornment position="end">
												<IconButton
													size="small"
													aria-label={t("system.dataGrid.clearColumnSearch")}
													onClick={() => setSearch("")}
													data-testid={testIdBase ? `${testIdBase}-search-clear` : undefined}
												>
													<CloseIcon sx={{ fontSize: 16, color: NEUTRAL[400] }} />
												</IconButton>
											</InputAdornment>
										) : undefined,
									},
								},
							}}
							inputSx={{
								...FILTER_SEARCH_CANCEL_BUTTON_SX,
								borderRadius: 2,
								backgroundColor: NEUTRAL[50],
								"& fieldset": { borderColor: "transparent" },
								"&:hover fieldset": { borderColor: NEUTRAL[200] },
								"&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
							}}
						/>
					</Box>

					<Box sx={{ flex: 1, overflowY: "auto", p: 1, minHeight: 0, maxHeight: 400 }}>
						{filteredGroups.length === 0 ? (
							<Box sx={{ p: 3, textAlign: "center", color: NEUTRAL[400], fontSize: 13 }}>{t("system.dataGrid.noMatchingColumns")}</Box>
						) : (
							filteredGroups.map((group) => {
								const listedVisibleCount = group.rows.filter(isEntryVisible).length;
								const allListedVisible = listedVisibleCount === group.rows.length;
								return (
									<Box key={group.key} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
										{group.label && (
											<Box
												sx={{
													px: 1,
													py: showGroupSelectAll ? 0.25 : 0.5,
													display: "flex",
													alignItems: "center",
													fontSize: 10,
													fontWeight: 700,
													letterSpacing: "0.08em",
													textTransform: "uppercase",
													color: NEUTRAL[400],
												}}
											>
												{showGroupSelectAll && (
													<Checkbox
														checked={allListedVisible}
														indeterminate={listedVisibleCount > 0 && !allListedVisible}
														size="small"
														disableRipple
														onChange={() => toggleEntries(group.rows, !allListedVisible)}
														slotProps={{ input: { "aria-label": t("system.dataGrid.selectAllInGroup", { group: group.label }) } }}
														data-testid={testIdBase ? `${testIdBase}-group-${group.key}-select-all` : undefined}
														sx={{ p: 0.5, mr: 1 }}
													/>
												)}
												{group.label}
											</Box>
										)}
										<List dense disablePadding>
											{group.rows.map((entry) => {
												/** The visibility model is a sparse object where `false` means hidden and `undefined` means visible!
												 *** Do not simplify this check.
												 * */
												const checked = isEntryVisible(entry);
												const { label, trendField } = entry;
												const trendChecked = trendField ? visibilityModel[trendField] !== false : false;
												return (
													<ListItemButton
														key={entry.key}
														onClick={() => toggleEntry(entry, !checked)}
														sx={{ borderRadius: 1.5, py: 0.25, px: 1, display: "flex", alignItems: "center" }}
														data-testid={testIdBase ? `${testIdBase}-option-${entry.testId}` : undefined}
													>
														<Checkbox checked={checked} size="small" disableRipple slotProps={{ input: { "aria-label": label } }} sx={{ p: 0.5, mr: 1 }} />
														<Box sx={{ flex: 1, fontSize: 13, fontWeight: 500, color: INK.PRIMARY }}>{label}</Box>
														{trendField && (
															<Box
																onClick={(e) => e.stopPropagation()}
																sx={{ display: "flex", alignItems: "center", color: NEUTRAL[400], fontSize: 11, fontWeight: 600 }}
															>
																{t("system.dataGrid.trend")}
																<Checkbox
																	checked={trendChecked}
																	disabled={!checked}
																	size="small"
																	disableRipple
																	onChange={() => toggleTrend(trendField, !trendChecked)}
																	slotProps={{ input: { "aria-label": `${label} ${t("system.dataGrid.trend")}` } }}
																	data-testid={testIdBase ? `${testIdBase}-trend-toggle-${trendField}` : undefined}
																	sx={{ p: 0.5, ml: 0.25 }}
																/>
															</Box>
														)}
													</ListItemButton>
												);
											})}
										</List>
									</Box>
								);
							})
						)}
					</Box>

					<Box
						sx={{
							p: 1,
							borderTop: `1px solid ${NEUTRAL[100]}`,
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<SystemButton size="small" buttonVariant="secondary" onClick={onReset} data-testid={testIdBase ? `${testIdBase}-reset` : undefined}>
							{t("system.dataGrid.resetColumnVisibility")}
						</SystemButton>
						<SystemButton size="small" buttonVariant="primary" onClick={closePopover} data-testid={testIdBase ? `${testIdBase}-done` : undefined}>
							{t("done")}
						</SystemButton>
					</Box>
				</Box>
			}
		>
			<ToolbarTriggerButton
				label={t("system.dataGrid.columns")}
				description={`${t("system.dataGrid.columns")} (${visibleCount})`}
				icon={<ViewColumnIcon />}
				count={visibleCount}
				iconOnly={onlyShowIcon}
				disabled={disabled}
				onClick={(e) => (open ? closePopover(e) : setAnchorEl(e.currentTarget))}
				dataTestId={testIdBase ? `${testIdBase}-trigger` : undefined}
			/>
		</SystemReactTinyPopover>
	);
}
